import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  MoreVert as MoreVertIcon,
  Share as ShareIcon,
  Image as ImageIcon,
  ContentCopy as ContentCopyIcon,
  ExitToApp as ExitToAppIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { Calendar, ArrowLeft } from 'lucide-react';
import { FirebaseService } from '../../../services/FirebaseService';
import { useAuth } from '../../Auth/AuthContext';
import { UserProfile, SharedReport, CompanyJoinRequest, CompanyRole, Company } from '../../../types/models';
import { format } from 'date-fns';
import { CompanyImagesManager } from './CompanyImages/CompanyImagesManager';
import '../MyReports/ReportsGrid.css';

// SharedReportCard component
interface SharedReportCardProps {
  report: SharedReport;
  onViewReport: (reportId: string) => void;
  onViewPDF: (reportId: string) => void;
}

const SharedReportCard: React.FC<SharedReportCardProps> = ({ report, onViewReport, onViewPDF }) => {
  //console.log('🃏 SharedReportCard: Rendering report card for:', report.id, report.title);
  const entryCount = report.entries?.length || 0;

  const handleViewEntries = () => {
    onViewReport(report.id);
  };

  const handleViewPDF = () => {
    onViewPDF(report.id);
  };

  return (
    <div className="report-card">
      <div className="report-card-header">
        <h3 className="report-title">{report.title}</h3>
        <div className="report-date">
          <span className="calendar-icon"><Calendar size={14} /></span>
          <span className="date-text">
            {format(report.createdAt, 'MMMM d, yyyy')} • {format(report.createdAt, 'h:mm a')}
          </span>
        </div>
      </div>

      <div className="report-card-content">
        <div className="report-shared-info">
          <div className="sharer-info">
            <div className="sharer-avatar">
              {report.sharerProfileImageUrl ? (
                <img 
                  src={report.sharerProfileImageUrl} 
                  alt={report.sharerDisplayName || 'User'} 
                  className="sharer-profile-image"
                />
              ) : (
                <span className="sharer-initials">
                  {report.sharerInitials || report.sharerDisplayName?.split(' ').map((n: string) => n[0]).join('').toUpperCase() || report.sharerEmail?.[0]?.toUpperCase() || 'U'}
                </span>
              )}
            </div>
            <div className="sharer-details">
              <span className="sharer-name">
                Shared by {report.sharerDisplayName || report.sharerEmail || 'Team Member'}
              </span>
              <span className="shared-date">
                {new Date(report.sharedAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        <div className="report-stats">
          <span className="entry-count">{entryCount} {entryCount === 1 ? 'entry' : 'entries'}</span>
        </div>

        <div className="report-card-actions">
          <button onClick={handleViewEntries} className="action-btn btn-view">
            View Entries
          </button>
          <button onClick={handleViewPDF} className="action-btn btn-edit">
            View PDF
          </button>
        </div>
      </div>
    </div>
  );
};

interface SharedReportsProps {
  onViewReport?: (reportId: string) => void;
  onViewPDF?: (reportId: string) => void;
  viewMode?: 'reports' | 'company-images';
  onViewModeChange?: (mode: 'reports' | 'company-images') => void;
}

const SharedReports: React.FC<SharedReportsProps> = ({ onViewReport, onViewPDF, viewMode = 'reports', onViewModeChange }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [sharedReports, setSharedReports] = useState<SharedReport[]>([]);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [companyMembers, setCompanyMembers] = useState<any[]>([]);
  const [joinRequests, setJoinRequests] = useState<CompanyJoinRequest[]>([]);
  const [showJoinRequestsDialog, setShowJoinRequestsDialog] = useState(false);
  const [isRefreshingSharedReports, setIsRefreshingSharedReports] = useState(false);
  const [refreshTimer, setRefreshTimer] = useState<NodeJS.Timeout | null>(null);
  const [companyDetails, setCompanyDetails] = useState<Company | null>(null);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  
  // Company menu state
  const [companyMenuAnchor, setCompanyMenuAnchor] = useState<null | HTMLElement>(null);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [isUpdatingCompany, setIsUpdatingCompany] = useState(false);
  
  // Dialog states
  const [showCreateCompanyDialog, setShowCreateCompanyDialog] = useState(false);
  const [showJoinCompanyDialog, setShowJoinCompanyDialog] = useState(false);
  const [createCompanyName, setCreateCompanyName] = useState('');
  const [createCompanyPassword, setCreateCompanyPassword] = useState('');
  const [joinCompanyCode, setJoinCompanyCode] = useState('');
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  const [isJoiningCompany, setIsJoiningCompany] = useState(false);

  // Handler functions for shared report actions
  const handleViewReport = (reportId: string) => {
    if (onViewReport) {
      onViewReport(reportId);
    } else {
      console.warn('onViewReport handler not provided');
    }
  };

  const handleViewPDF = (reportId: string) => {
    if (onViewPDF) {
      onViewPDF(reportId);
    } else {
      console.warn('onViewPDF handler not provided');
    }
  };

  const closeError = () => setError('');
  const closeSuccess = () => setSuccess('');

  // Load company details to get the company code
  const loadCompanyDetails = async (profile?: UserProfile | null) => {
    const currentProfile = profile || userProfile;
    if (!currentProfile?.companyId) {
      setCompanyDetails(null);
      return;
    }
    
    try {
      //console.log('🔍 SharedReports: Loading company details for company:', currentProfile.companyId);
      const company = await FirebaseService.getCompanyDetails(currentProfile.companyId);
      setCompanyDetails(company);
      //console.log('✅ SharedReports: Company details loaded:', company);
    } catch (error: any) {
      console.error('❌ SharedReports: Error loading company details:', error);
      // Don't show error to user for this, it's not critical
    }
  };

  // Copy company code to clipboard
  const handleCopyCompanyCode = async () => {
    if (!companyDetails?.code) return;
    
    try {
      await navigator.clipboard.writeText(companyDetails.code);
      setCopySnackbarOpen(true);
    } catch (error) {
      console.error('Failed to copy company code:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = companyDetails.code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySnackbarOpen(true);
    }
  };

  const loadCompanyMembers = async (profile?: UserProfile | null) => {
    try {
      const currentProfile = profile || userProfile;
      if (currentProfile?.companyId) {
        //console.log('🔍 SharedReports: Loading company members for company:', currentProfile.companyId);
        const members = await FirebaseService.getCompanyMembers(currentProfile.companyId);
        //console.log('🔍 SharedReports: Raw members returned:', members);
        setCompanyMembers(members);
        //console.log('✅ SharedReports: Set', members.length, 'company members in state');
      } else {
        //console.log('⚠️ SharedReports: No companyId found in profile:', currentProfile);
      }
    } catch (error: any) {
      console.error('❌ SharedReports: Failed to load company members:', error);
      // Show error to user for debugging
      setError(`Failed to load company members: ${error.message}`);
    }
  };

  const loadJoinRequests = async (profile?: UserProfile | null) => {
    const currentProfile = profile || userProfile;
    try {
      //console.log('🔍 SharedReports: loadJoinRequests called with profile:', currentProfile);
      if (currentProfile?.companyId && (currentProfile.companyRole === CompanyRole.OWNER || currentProfile.companyRole === CompanyRole.ADMIN)) {
        //console.log('🔍 SharedReports: Loading join requests for company:', currentProfile.companyId, 'Role:', currentProfile.companyRole);
        const requests = await FirebaseService.getPendingJoinRequests(currentProfile.companyId);
        setJoinRequests(requests);
        //console.log('✅ SharedReports: Loaded', requests.length, 'join requests:', requests);
      } else {
        //console.log('⚠️ SharedReports: Not loading join requests - companyId:', currentProfile?.companyId, 'role:', currentProfile?.companyRole);
      }
    } catch (error: any) {
      console.error('❌ SharedReports: Failed to load join requests:', error);
      // Don't show error to user for join requests - this is not critical
    }
  };

  const handleApproveRequest = async (requestId: string) => {
    try {
      if (!userProfile?.companyId) {
        throw new Error('Company ID not found');
      }
      await FirebaseService.approveJoinRequest(requestId, userProfile.companyId);
      setSuccess('Join request approved successfully');
      loadJoinRequests(); // Refresh the list
      loadCompanyMembers(); // Refresh members list
    } catch (error: any) {
      setError(`Failed to approve request: ${error.message}`);
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    try {
      if (!userProfile?.companyId) {
        throw new Error('Company ID not found');
      }
      await FirebaseService.rejectJoinRequest(requestId, userProfile.companyId);
      setSuccess('Join request rejected');
      loadJoinRequests(); // Refresh the list
    } catch (error: any) {
      setError(`Failed to reject request: ${error.message}`);
    }
  };

  // Company creation handler
  const handleCreateCompany = async () => {
    if (!createCompanyName.trim()) {
      setError('Please enter a company name');
      return;
    }

    if (createCompanyPassword !== 'denise') {
      setError('Invalid password. Please contact support for company creation access.');
      return;
    }

    setIsCreatingCompany(true);
    try {
      //console.log('🏢 Creating company:', createCompanyName);
      const company = await FirebaseService.createCompany(createCompanyName.trim());
      //console.log('✅ Company created:', company);
      
      setSuccess(`Company "${createCompanyName}" created successfully!`);
      setShowCreateCompanyDialog(false);
      setCreateCompanyName('');
      setCreateCompanyPassword('');
      
      // Refresh user profile to reflect new company membership
      const updatedProfile = await loadUserProfile();
      // Also refresh company details to ensure fresh data is loaded
      if (updatedProfile?.companyId) {
        await loadCompanyDetails(updatedProfile);
      }
    } catch (error: any) {
      console.error('❌ Failed to create company:', error);
      setError(`Failed to create company: ${error.message}`);
    } finally {
      setIsCreatingCompany(false);
    }
  };

  // Company join handler  
  const handleJoinCompany = async () => {
    if (!joinCompanyCode.trim() || joinCompanyCode.length !== 6) {
      setError('Please enter a valid 6-character company code');
      return;
    }

    setIsJoiningCompany(true);
    try {
      //console.log('🤝 Joining company with code:', joinCompanyCode);
      await FirebaseService.joinCompany(joinCompanyCode.trim().toUpperCase());
      
      setSuccess('Join request submitted! Company administrators will review your request.');
      setShowJoinCompanyDialog(false);
      setJoinCompanyCode('');
    } catch (error: any) {
      console.error('❌ Failed to join company:', error);
      setError(`Failed to join company: ${error.message}`);
    } finally {
      setIsJoiningCompany(false);
    }
  };

  // Company menu handlers
  const handleCompanyMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCompanyMenuAnchor(event.currentTarget);
  };

  const handleCompanyMenuClose = () => {
    setCompanyMenuAnchor(null);
  };

  const handleRenameCompany = async () => {
    if (!newCompanyName.trim()) {
      setError('Please enter a company name');
      return;
    }

    setIsUpdatingCompany(true);
    try {
      if (userProfile?.companyId) {
        //console.log('🔄 Renaming company to:', newCompanyName.trim());
        
        // Update company name and get back the updated profile
        const updatedProfile = await FirebaseService.updateCompanyName(userProfile.companyId, newCompanyName.trim());
        
        if (updatedProfile) {
          // Use the updated profile returned from the service
          setUserProfile(updatedProfile);
          //console.log('✅ Company name updated in UI:', updatedProfile.companyName);
        }
        
        setSuccess('Company renamed successfully!');
        setShowRenameDialog(false);
        setNewCompanyName('');
        
        // Also refresh company details to ensure consistency
        await loadCompanyDetails(updatedProfile);
      }
    } catch (error: any) {
      console.error('❌ Error renaming company:', error);
      setError(`Failed to rename company: ${error.message}`);
    } finally {
      setIsUpdatingCompany(false);
    }
  };

  const handleDeleteCompany = async () => {
    try {
      // deleteCompany() gets company ID from current user profile
      await FirebaseService.deleteCompany();
      setSuccess('Company deleted successfully!');
      setShowDeleteDialog(false);
      
      // Clear local state first
      setUserProfile(null);
      setCompanyDetails(null);
      setSharedReports([]);
      setCompanyMembers([]);
      setJoinRequests([]);
      
      // Refresh user profile to reflect the change
      await loadUserProfile();
    } catch (error: any) {
      setError(`Failed to delete company: ${error.message}`);
    }
  };

  const handleLeaveCompany = async () => {
    try {
      // leaveCompany() gets company ID from current user profile  
      await FirebaseService.leaveCompany();
      setSuccess('Left company successfully!');
      setShowLeaveDialog(false);
      // Refresh user profile to reflect the change
      await loadUserProfile();
    } catch (error: any) {
      setError(`Failed to leave company: ${error.message}`);
    }
  };

  // Load user profile function - like iOS loadUserProfile
  const loadUserProfile = async () => {
    if (!user) return;
    
    try {
      //console.log('🔍 SharedReports: Loading user profile...');
      const profile = await FirebaseService.fetchUserProfile();
      
      // If profile shows no company but user might be in one, check for sync issues
      if (!profile?.companyId) {
        //console.log('🔧 SharedReports: No company found, checking for sync issues...');
        try {
          await FirebaseService.fixUserProfileSync(user.uid);
          // Reload profile after potential fix
          const updatedProfile = await FirebaseService.fetchUserProfile();
          setUserProfile(updatedProfile);
          //console.log('✅ SharedReports: Profile updated after sync check:', updatedProfile);
          return updatedProfile;
        } catch (syncError) {
          //console.log('🔧 SharedReports: Sync check completed, no changes needed');
        }
      }
      
      setUserProfile(profile);
      //console.log('✅ SharedReports: User profile loaded:', profile);
      return profile;
    } catch (error: any) {
      console.error('❌ SharedReports: Error loading user profile:', error);
      
      // Handle permission errors gracefully like iOS app
      if (error.code === 'permission-denied' || error.message?.includes('permission')) {
        //console.log('🔐 SharedReports: Permission denied, user may not have profile yet');
        // Don't show error to user for permission issues during initial load
        return null;
      }
      
      setError(`Failed to load profile: ${error.message}`);
      return null;
    }
  };

  // Load shared reports function - like iOS loadSharedReports
  const loadSharedReports = async (profile?: UserProfile | null) => {
    const currentProfile = profile || userProfile;
    if (!currentProfile?.companyId) {
      setSharedReports([]);
      return;
    }
    
    try {
      //console.log('🔍 SharedReports: Loading shared reports for company:', currentProfile.companyId);
      setIsRefreshingSharedReports(true);
      const reports = await FirebaseService.getSharedReports();
      setSharedReports(reports);
      //console.log('✅ SharedReports: Loaded', reports.length, 'shared reports');
    } catch (error: any) {
      console.error('❌ SharedReports: Error loading shared reports:', error);
      setError(`Failed to load shared reports: ${error.message}`);
    } finally {
      setIsRefreshingSharedReports(false);
    }
  };

  // Initial load effect - similar to iOS onAppear
  useEffect(() => {
    const initializeComponent = async () => {
      if (user) {
        setLoading(true);
        try {
          // Load user profile first
          const profile = await loadUserProfile();
          
          if (profile?.companyId) {
            // Load company-related data if user is in a company
            await Promise.all([
              loadSharedReports(profile),
              loadCompanyMembers(profile),
              loadCompanyDetails(profile),
              // Only load join requests for owners/admins
              profile.companyRole === CompanyRole.OWNER || profile.companyRole === CompanyRole.ADMIN 
                ? loadJoinRequests(profile) 
                : Promise.resolve()
            ]);
          }
        } catch (error: any) {
          console.error('❌ SharedReports: Error during initialization:', error);
          // Only show errors that aren't permission-related
          if (!error.message?.includes('permission')) {
            setError(error.message || 'Failed to initialize shared reports');
          }
        } finally {
          setLoading(false);
        }
      }
    };

    initializeComponent();

    // Set up periodic refresh like iOS app - every 10 seconds
    // This helps detect when a user is approved for a company
    if (refreshTimer) {
      clearInterval(refreshTimer);
    }
    
    const timer = setInterval(async () => {
      if (user && userProfile?.companyId) {
        try {
          const updatedProfile = await loadUserProfile();
          // Also refresh company details to pick up name changes, new members, etc.
          if (updatedProfile?.companyId) {
            await loadCompanyDetails(updatedProfile);
          }
        } catch (error) {
          //console.log('Background refresh failed:', error);
        }
      }
    }, 10000); // 10 seconds like iOS

    setRefreshTimer(timer);

    // Cleanup function
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [user]);

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
    };
  }, [refreshTimer]);

  if (loading && !userProfile) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Company images view
  if (viewMode === 'company-images') {
    return (
      <>
        {/* Back button */}
        <Box sx={{ mb: 3 }}>
          <Button 
            startIcon={<ArrowLeft />}
            onClick={() => onViewModeChange?.('reports')}
            sx={{
              color: 'var(--primary-dark-blue)',
              '&:hover': {
                backgroundColor: 'var(--soft-beige)',
              }
            }}
          >
            Back to Shared Reports
          </Button>
        </Box>

        {/* Company Images Manager */}
        <CompanyImagesManager />

        {/* Error/Success Alerts */}
        {error && (
          <Alert severity="error" onClose={closeError} sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={closeSuccess} sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
      </>
    );
  }

  // No company view
  if (!userProfile?.companyId) {
    return (
      <>
        <Paper sx={{ 
          p: 4, 
          textAlign: 'center', 
          maxWidth: 600, 
          mx: 'auto', 
          mt: 4,
          background: 'var(--neutral-white)',
          borderRadius: '12px',
          border: '1px solid rgba(46, 125, 77, 0.2)',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <BusinessIcon sx={{ fontSize: 64, color: 'var(--accent-green)', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ color: 'var(--primary-dark-blue)', fontWeight: 'bold' }}>
            Join a Company to Access Shared Reports
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(27, 46, 60, 0.7)' }} paragraph>
            Create your own company or join an existing one to collaborate and share reports with your team.
          </Typography>
              
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateCompanyDialog(true)}
              sx={{
                backgroundColor: 'var(--accent-green)',
                color: 'white',
                fontWeight: 'semibold',
                borderRadius: '12px',
                px: 3,
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(46, 125, 77, 0.9)',
                }
              }}
            >
              Create Company
            </Button>
            <Button
              variant="outlined"
              startIcon={<GroupIcon />}
              onClick={() => setShowJoinCompanyDialog(true)}
              sx={{
                borderColor: 'var(--accent-green)',
                color: 'var(--accent-green)',
                fontWeight: 'semibold',
                borderRadius: '12px',
                px: 3,
                py: 1.5,
                '&:hover': {
                  borderColor: 'var(--accent-green)',
                  backgroundColor: 'rgba(46, 125, 77, 0.1)',
                }
              }}
            >
              Join Company
            </Button>
          </Box>
        </Paper>

        {/* Error/Success Alerts */}
        {error && (
          <Alert severity="error" onClose={closeError} sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={closeSuccess} sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        {/* Create Company Dialog */}
        <Dialog 
          open={showCreateCompanyDialog} 
          onClose={() => setShowCreateCompanyDialog(false)}
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              backgroundColor: 'var(--neutral-white)'
            }
          }}
        >
          <DialogTitle sx={{ 
            color: 'var(--primary-dark-blue)', 
            fontWeight: 'bold',
            textAlign: 'center',
            pb: 1
          }}>
            Create Your Company
          </DialogTitle>
          <DialogContent sx={{ 
            backgroundColor: 'var(--neutral-white)',
            px: 3,
            pb: 2
          }}>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Company Name"
                value={createCompanyName}
                onChange={(e) => setCreateCompanyName(e.target.value)}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--accent-green)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--accent-green)',
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'var(--accent-green)',
                  }
                }}
                placeholder="Enter your company name"
                autoFocus
              />
              
              <TextField
                fullWidth
                label="Creation Password"
                type="password"
                value={createCompanyPassword}
                onChange={(e) => setCreateCompanyPassword(e.target.value)}
                variant="outlined"
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--accent-green)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--accent-green)',
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'var(--accent-green)',
                  }
                }}
                placeholder="Enter password to create company"
              />
              
              <Typography variant="body2" sx={{ 
                mt: 2, 
                color: 'rgba(27, 46, 60, 0.7)',
                textAlign: 'center'
              }}>
                You'll be the company owner and can invite team members after creation.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3,
            backgroundColor: 'var(--neutral-white)',
            gap: 1
          }}>
            <Button 
              onClick={() => {
                setShowCreateCompanyDialog(false);
                setCreateCompanyName('');
                setCreateCompanyPassword('');
              }}
              sx={{ 
                color: 'rgba(27, 46, 60, 0.7)',
                fontWeight: 'medium',
                px: 3,
                py: 1.5,
                borderRadius: '12px'
              }}
              disabled={isCreatingCompany}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCompany}
              disabled={!createCompanyName.trim() || !createCompanyPassword.trim() || isCreatingCompany}
              variant="contained"
              sx={{
                backgroundColor: 'var(--accent-green)',
                '&:hover': {
                  backgroundColor: 'rgba(46, 125, 77, 0.9)'
                },
                fontWeight: 'semibold',
                px: 3,
                py: 1.5,
                borderRadius: '12px',
                minWidth: '140px'
              }}
            >
              {isCreatingCompany ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Create Company'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Join Company Dialog */}
        <Dialog 
          open={showJoinCompanyDialog} 
          onClose={() => setShowJoinCompanyDialog(false)}
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              backgroundColor: 'var(--neutral-white)'
            }
          }}
        >
          <DialogTitle sx={{ 
            color: 'var(--primary-dark-blue)', 
            fontWeight: 'bold',
            textAlign: 'center',
            pb: 1
          }}>
            Join a Company
          </DialogTitle>
          <DialogContent sx={{ 
            backgroundColor: 'var(--neutral-white)',
            px: 3,
            pb: 2
          }}>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Company Code"
                value={joinCompanyCode}
                onChange={(e) => setJoinCompanyCode(e.target.value.toUpperCase())}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--accent-green)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--accent-green)',
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'var(--accent-green)',
                  },
                  '& .MuiOutlinedInput-input': {
                    textAlign: 'center',
                    fontSize: '1.2rem',
                    letterSpacing: '3px',
                    fontWeight: 'bold'
                  }
                }}
                placeholder="ABC123"
                inputProps={{ maxLength: 6 }}
                autoFocus
              />
              <Typography variant="body2" sx={{ 
                mt: 2, 
                color: 'rgba(27, 46, 60, 0.7)',
                textAlign: 'center'
              }}>
                Ask your company administrator for the 6-character company code.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3,
            backgroundColor: 'var(--neutral-white)',
            gap: 1
          }}>
            <Button 
              onClick={() => {
                setShowJoinCompanyDialog(false);
                setJoinCompanyCode('');
              }}
              sx={{ 
                color: 'rgba(27, 46, 60, 0.7)',
                fontWeight: 'medium',
                px: 3,
                py: 1.5,
                borderRadius: '12px'
              }}
              disabled={isJoiningCompany}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleJoinCompany}
              disabled={joinCompanyCode.length !== 6 || isJoiningCompany}
              variant="contained"
              sx={{
                backgroundColor: 'var(--accent-green)',
                '&:hover': {
                  backgroundColor: 'rgba(46, 125, 77, 0.9)'
                },
                fontWeight: 'semibold',
                px: 3,
                py: 1.5,
                borderRadius: '12px',
                minWidth: '140px'
              }}
            >
              {isJoiningCompany ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Send Request'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Company member view (reports)
  return (
    <>
      {/* Company Info Header */}
      <Paper sx={{ 
        p: 4, 
        mb: 3, 
        background: '#ffffff',
        borderRadius: '20px !important', // More rounded like PDF pages
        border: '1px solid rgba(46, 125, 77, 0.15)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)', // Softer shadow like PDF
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
          borderColor: 'rgba(46, 125, 77, 0.25)'
        }
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Typography variant="h6" sx={{ 
                color: 'var(--primary-dark-blue)', 
                fontWeight: 'bold',
                mb: 0
              }}>
                {companyDetails?.name || userProfile.companyName}
              </Typography>
              {companyDetails?.code && (
                <Box 
                  onClick={handleCopyCompanyCode}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    cursor: 'pointer',
                    padding: '4px 8px',
                    backgroundColor: 'rgba(46, 125, 77, 0.1)',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(46, 125, 77, 0.2)',
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  <Typography variant="caption" sx={{ 
                    color: 'var(--accent-green)', 
                    fontWeight: 'bold',
                    fontSize: '0.75rem'
                  }}>
                    {companyDetails.code}
                  </Typography>
                  <ContentCopyIcon sx={{ 
                    fontSize: 12, 
                    color: 'var(--accent-green)' 
                  }} />
                </Box>
              )}
            </Box>
            <Box display="flex" gap={1} alignItems="center" mb={1}>
              <Chip 
                label={`Your role: ${userProfile.companyRole}`}
                sx={{ 
                  backgroundColor: 'var(--accent-green)', 
                  color: 'white',
                  '& .MuiChip-label': { color: 'white', fontWeight: 'medium' },
                  fontSize: '0.75rem'
                }}
                size="small" 
              />
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center" gap={2}>
            <BusinessIcon sx={{ 
              fontSize: '2rem', 
              color: 'var(--accent-green)' 
            }} />
            <IconButton 
              sx={{ color: 'var(--accent-green)' }}
              onClick={handleCompanyMenuOpen}
            >
              <MoreVertIcon />
            </IconButton>

            {/* Company Menu */}
            <Menu
              anchorEl={companyMenuAnchor}
              open={Boolean(companyMenuAnchor)}
              onClose={handleCompanyMenuClose}
              PaperProps={{
                sx: {
                  borderRadius: '12px',
                  minWidth: '180px'
                }
              }}
            >
              {userProfile?.companyRole === CompanyRole.OWNER ? (
                <>
                  <MenuItem onClick={() => {
                    handleCompanyMenuClose();
                    setNewCompanyName(companyDetails?.name || userProfile?.companyName || '');
                    setShowRenameDialog(true);
                  }}>
                    <ListItemIcon>
                      <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Rename Company</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleCompanyMenuClose();
                    setShowDeleteDialog(true);
                  }} sx={{ color: 'error.main' }}>
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    <ListItemText>Delete Company</ListItemText>
                  </MenuItem>
                </>
              ) : (
                <MenuItem onClick={() => {
                  handleCompanyMenuClose();
                  setShowLeaveDialog(true);
                }} sx={{ color: 'var(--accent-green)' }}>
                  <ListItemIcon>
                    <ExitToAppIcon fontSize="small" sx={{ color: 'var(--accent-green)' }} />
                  </ListItemIcon>
                  <ListItemText>Leave Company</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Box>
      </Paper>

      {/* Join Requests Banner */}
      {joinRequests.length > 0 && (userProfile?.companyRole === CompanyRole.OWNER || userProfile?.companyRole === CompanyRole.ADMIN) && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3,
            borderRadius: '12px',
            border: '1px solid rgba(46, 125, 77, 0.3)',
            backgroundColor: 'rgba(46, 125, 77, 0.05)'
          }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => setShowJoinRequestsDialog(true)}
              sx={{ fontWeight: 'bold' }}
            >
              View Requests ({joinRequests.length})
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            You have {joinRequests.length} pending join request{joinRequests.length > 1 ? 's' : ''} for your company
          </Typography>
        </Alert>
      )}

      {/* Management Section */}
      <Paper sx={{ 
        p: 4, 
        mb: 3,
        background: '#ffffff',
        borderRadius: '20px !important', // More rounded like PDF pages
        border: '1px solid rgba(46, 125, 77, 0.15)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)', // Softer shadow like PDF
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
          borderColor: 'rgba(46, 125, 77, 0.25)'
        }
      }}>
        <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(150px, 1fr))" gap={2}>
          {/* Company Assets */}
          <Button 
            variant="outlined"
            onClick={() => {
              //console.log('🏢 Company Assets button clicked, switching to company-images mode');
              onViewModeChange?.('company-images');
            }}
            sx={{ 
              p: 2,
              height: '80px',
              flexDirection: 'column',
              gap: 1,
              backgroundColor: 'var(--neutral-white)',
              borderColor: 'rgba(27, 46, 60, 0.2)',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'var(--neutral-white)',
                borderColor: 'var(--accent-green)',
              }
            }}
          >
            <ImageIcon sx={{ fontSize: 24, color: 'var(--accent-green)' }} />
            <Typography variant="caption" sx={{ 
              color: 'var(--primary-dark-blue)', 
              fontWeight: 'medium',
              textTransform: 'none'
            }}>
              Company Assets
            </Typography>
          </Button>
          
          {/* Join Requests - Only show for owners/admins */}
          {(userProfile?.companyRole === CompanyRole.OWNER || userProfile?.companyRole === CompanyRole.ADMIN) && (
            <Button 
              variant="outlined"
              onClick={() => setShowJoinRequestsDialog(true)}
              sx={{ 
                p: 2,
                height: '80px',
                flexDirection: 'column',
                gap: 1,
                backgroundColor: 'var(--neutral-white)',
                borderColor: 'rgba(27, 46, 60, 0.2)',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'var(--neutral-white)',
                  borderColor: 'var(--accent-green)',
                },
                position: 'relative'
              }}
            >
              <AddIcon sx={{ fontSize: 24, color: 'var(--accent-green)' }} />
              <Typography variant="caption" sx={{ 
                color: 'var(--primary-dark-blue)', 
                fontWeight: 'medium',
                textTransform: 'none'
              }}>
                Requests ({joinRequests.length})
              </Typography>
            </Button>
          )}
          
          {/* Members Management */}
          <Button 
            variant="outlined"
            onClick={() => {
              //console.log('🔍 Members button clicked. Current companyMembers state:', companyMembers);
              //console.log('🔍 companyMembers length:', companyMembers.length);
              setShowMembersDialog(true);
            }}
            sx={{ 
              p: 2,
              height: '80px',
              flexDirection: 'column',
              gap: 1,
              backgroundColor: 'var(--neutral-white)',
              borderColor: 'rgba(27, 46, 60, 0.2)',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'var(--neutral-white)',
                borderColor: 'var(--accent-green)',
              }
            }}
          >
            <GroupIcon sx={{ fontSize: 24, color: 'var(--accent-green)' }} />
            <Typography variant="caption" sx={{ 
              color: 'var(--primary-dark-blue)', 
              fontWeight: 'medium',
              textTransform: 'none'
            }}>
              Members ({companyMembers.length})
            </Typography>
          </Button>
        </Box>
      </Paper>

      {/* Shared Reports Section */}
      <Paper sx={{ 
        p: 4, 
        mb: 3,
        background: '#ffffff',
        borderRadius: '20px !important', // More rounded like PDF pages
        border: '1px solid rgba(46, 125, 77, 0.15)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)', // Softer shadow like PDF
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
          borderColor: 'rgba(46, 125, 77, 0.25)'
        }
      }}>
        <Typography variant="h6" gutterBottom sx={{ 
          color: 'var(--primary-dark-blue)', 
          fontWeight: 'bold',
          mb: 3
        }}>
          Shared Reports
        </Typography>
        
        {loading || isRefreshingSharedReports ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2, color: 'rgba(27, 46, 60, 0.7)' }}>
              {loading ? 'Loading shared reports...' : 'Refreshing...'}
            </Typography>
          </Box>
        ) : sharedReports.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ShareIcon sx={{ fontSize: 48, color: 'var(--accent-green)', mb: 2 }} />
            <Typography variant="body1" sx={{ color: 'rgba(27, 46, 60, 0.7)' }}>
              No shared reports yet. Share reports from your personal collection to collaborate with your team.
            </Typography>
            {/* Debug info */}
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'rgba(27, 46, 60, 0.5)' }}>
              Debug: {sharedReports.length} reports loaded, Company ID: {userProfile?.companyId || 'none'}
            </Typography>
          </Box>
        ) : (
          <div className="reports-grid">
            {sharedReports.map((report) => (
              <SharedReportCard 
                key={report.id} 
                report={report}
                onViewReport={handleViewReport}
                onViewPDF={handleViewPDF}
              />
            ))}
          </div>
        )}
      </Paper>

      {/* Join Requests Dialog */}
      <Dialog 
        open={showJoinRequestsDialog} 
        onClose={() => setShowJoinRequestsDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            backgroundColor: 'var(--soft-beige)'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'var(--primary-dark-blue)', 
          fontWeight: 'bold',
          textAlign: 'center'
        }}>
          Join Requests ({joinRequests.length})
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'var(--soft-beige)' }}>
          {joinRequests.length === 0 ? (
            <Typography sx={{ color: 'rgba(27, 46, 60, 0.7)', textAlign: 'center', py: 4 }}>
              No pending join requests.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {joinRequests.map((request) => (
                <Paper key={request.id} sx={{ 
                  p: 3,
                  backgroundColor: 'var(--neutral-white)',
                  borderRadius: '8px',
                  border: '1px solid rgba(27, 46, 60, 0.1)'
                }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" sx={{ color: 'var(--primary-dark-blue)', fontWeight: 'bold' }}>
                        {request.userEmail}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(27, 46, 60, 0.6)' }}>
                        Requested on {new Date(request.requestedAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" gap={1}>
                      <Button 
                        variant="contained" 
                        color="success" 
                        size="small"
                        onClick={() => handleApproveRequest(request.id)}
                        sx={{ 
                          backgroundColor: 'var(--accent-green)',
                          '&:hover': { backgroundColor: 'rgba(46, 125, 77, 0.9)' }
                        }}
                      >
                        Approve
                      </Button>
                      <Button 
                        variant="outlined" 
                        color="error" 
                        size="small"
                        onClick={() => handleRejectRequest(request.id)}
                      >
                        Reject
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 3,
          backgroundColor: 'var(--soft-beige)'
        }}>
          <Button 
            onClick={() => setShowJoinRequestsDialog(false)}
            sx={{ 
              color: 'var(--primary-dark-blue)',
              fontWeight: 'medium'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Members Dialog */}
      <Dialog 
        open={showMembersDialog} 
        onClose={() => setShowMembersDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            backgroundColor: 'var(--soft-beige)'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'var(--primary-dark-blue)', 
          fontWeight: 'bold',
          textAlign: 'center'
        }}>
          Company Members ({companyMembers.length})
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'var(--soft-beige)' }}>
          {(() => {
            //console.log('🔍 Dialog render - companyMembers:', companyMembers);
            //console.log('🔍 Dialog render - companyMembers.length:', companyMembers.length);
            //console.log('🔍 Dialog render - companyMembers type:', typeof companyMembers);
            return null;
          })()}
          {companyMembers.length === 0 ? (
            <Typography sx={{ color: 'rgba(27, 46, 60, 0.7)', textAlign: 'center', py: 4 }}>
              No members found.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {companyMembers.map((member, index) => {
                //console.log('🔍 Rendering member', index, ':', member);
                return (
                <Paper key={index} sx={{ 
                  p: 2,
                  backgroundColor: 'var(--neutral-white)',
                  borderRadius: '8px',
                  border: '1px solid rgba(27, 46, 60, 0.1)'
                }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    {/* Profile Picture */}
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(27, 46, 60, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}>
                      {member.profileImageUrl ? (
                        <img 
                          src={member.profileImageUrl} 
                          alt={member.displayName || 'User'} 
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                        />
                      ) : (
                        <Typography sx={{ 
                          fontSize: '0.9rem', 
                          fontWeight: 600, 
                          color: 'var(--primary-dark-blue)' 
                        }}>
                          {member.displayName?.charAt(0)?.toUpperCase() || member.email?.charAt(0)?.toUpperCase() || 'U'}
                        </Typography>
                      )}
                    </Box>
                    
                    {/* Member Info */}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ 
                        color: 'var(--primary-dark-blue)', 
                        fontWeight: 'bold' 
                      }}>
                        {member.displayName || member.email}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(27, 46, 60, 0.6)' }}>
                        {member.email}
                      </Typography>
                    </Box>
                    
                    {/* Role */}
                    <Chip 
                      label={member.role || 'Member'}
                      size="small"
                      sx={{
                        backgroundColor: member.role === 'OWNER' ? 'var(--accent-green)' : 'rgba(27, 46, 60, 0.1)',
                        color: member.role === 'OWNER' ? 'white' : 'var(--primary-dark-blue)',
                        fontWeight: 'medium'
                      }}
                    />
                  </Box>
                </Paper>
              );
            })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 3,
          backgroundColor: 'var(--soft-beige)'
        }}>
          <Button 
            onClick={() => setShowMembersDialog(false)}
            sx={{ 
              color: 'var(--primary-dark-blue)',
              fontWeight: 'semibold'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Company Dialog */}
      <Dialog 
        open={showRenameDialog} 
        onClose={() => setShowRenameDialog(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: 'var(--soft-beige)',
          fontWeight: 'bold',
          color: 'var(--primary-dark-blue)'
        }}>
          Rename Company
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'var(--soft-beige)', pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="Company Name"
            value={newCompanyName}
            onChange={(e) => setNewCompanyName(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          p: 3,
          backgroundColor: 'var(--soft-beige)'
        }}>
          <Button 
            onClick={() => setShowRenameDialog(false)}
            sx={{ 
              color: 'var(--primary-dark-blue)',
              fontWeight: 'semibold'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRenameCompany}
            variant="contained"
            disabled={!newCompanyName.trim() || loading}
            sx={{ 
              backgroundColor: 'var(--accent-green)',
              '&:hover': { backgroundColor: 'var(--accent-green-dark)' },
              fontWeight: 'semibold'
            }}
          >
            {loading ? <CircularProgress size={20} /> : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Company Confirmation Dialog */}
      <Dialog 
        open={showDeleteDialog} 
        onClose={() => setShowDeleteDialog(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: 'var(--soft-beige)',
          fontWeight: 'bold',
          color: 'error.main'
        }}>
          Delete Company
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'var(--soft-beige)', pt: 2 }}>
          <Typography>
            Are you sure you want to delete "{companyDetails?.name || userProfile?.companyName}"? This action cannot be undone and will remove all company data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3,
          backgroundColor: 'var(--soft-beige)'
        }}>
          <Button 
            onClick={() => setShowDeleteDialog(false)}
            sx={{ 
              color: 'var(--primary-dark-blue)',
              fontWeight: 'semibold'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteCompany}
            variant="contained"
            color="error"
            disabled={loading}
            sx={{ fontWeight: 'semibold' }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Leave Company Confirmation Dialog */}
      <Dialog 
        open={showLeaveDialog} 
        onClose={() => setShowLeaveDialog(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: 'var(--soft-beige)',
          fontWeight: 'bold',
          color: 'var(--accent-green)'
        }}>
          Leave Company
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'var(--soft-beige)', pt: 2 }}>
          <Typography>
            Are you sure you want to leave "{companyDetails?.name || userProfile?.companyName}"? You will lose access to all shared reports and company data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3,
          backgroundColor: 'var(--soft-beige)'
        }}>
          <Button 
            onClick={() => setShowLeaveDialog(false)}
            sx={{ 
              color: 'var(--primary-dark-blue)',
              fontWeight: 'semibold'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleLeaveCompany}
            variant="contained"
            disabled={loading}
            sx={{ 
              backgroundColor: 'var(--accent-green)',
              '&:hover': { backgroundColor: 'var(--accent-green-dark)' },
              fontWeight: 'semibold'
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Leave'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error/Success Alerts */}
      {error && (
        <Alert severity="error" onClose={closeError} sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={closeSuccess} sx={{ mt: 2 }}>
          {success}
        </Alert>
      )}
      
      {/* Copy confirmation snackbar */}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Company code copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      

    </>
  );
};

export default SharedReports;
